import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { faker } from '@faker-js/faker';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';
import { Backdrop, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { userInfoAtom } from '@/interfaces/member';
import { postOrderInfoAtom, postPaymentConfirmAtom } from '@/interfaces/payments';
import { postSubscriptionAtom } from '@/interfaces/subscription';

import type { TossPaymentsWidgets } from '@tosspayments/tosspayments-sdk';

interface PaymentDialogProps {
  sensorId: string;
  orderName: string;
  amount: number;
  onClose: (reason: string) => void;
}

interface TossError {
  message: string;
  code: string;
  orderId: string;
}

// FIXME: Use actual client key
const CLIENT_KEY = 'test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm';

export default function PaymentDialog({ sensorId, orderName, amount, onClose }: PaymentDialogProps) {
  const { data: userInfo } = useAtomValue(userInfoAtom);
  const { mutate: postOrderInfo, data: postOrderInfoSuccess } = useAtomValue(postOrderInfoAtom);
  const { mutate: postPaymentConfirm, data: postPaymentConfirmSuccess } = useAtomValue(postPaymentConfirmAtom);
  const { mutate: postSubscription, data: postSubscriptionSuccess } = useAtomValue(postSubscriptionAtom);

  const [ready, setReady] = useState(false);
  const [pending, setPending] = useState(false);
  const [widgets, setWidgets] = useState<TossPaymentsWidgets>(null);
  const [orderId, setOrderId] = useState('');
  const [paymentKey, setPaymentKey] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    async function fetchPaymentWidgets() {
      try {
        const tossPayments = await loadTossPayments(CLIENT_KEY);
        const widgets = tossPayments.widgets({ customerKey: userInfo.id });

        setWidgets(widgets);
      } catch (error) {
        window.alert(t('Devices.message.cannotLoadPayment'));
      }
    }

    fetchPaymentWidgets();
  }, [t, userInfo.id]);
  useEffect(() => {
    async function renderPaymentWidgets() {
      if (widgets == null) {
        return;
      }

      await widgets.setAmount({ currency: 'KRW', value: amount });
      await widgets.renderPaymentMethods({
        selector: '#payment-method',
        variantKey: 'DEFAULT',
      });
      await widgets.renderAgreement({
        selector: '#agreement',
        variantKey: 'AGREEMENT',
      });

      setReady(true);
    }

    if (!ready) renderPaymentWidgets();
  }, [amount, ready, widgets]);

  useEffect(() => {
    if (postOrderInfoSuccess) {
      postPaymentConfirm({ paymentKey, orderId, amount });
    } else if (postOrderInfoSuccess === false) {
      window.alert(t('Devices.message.purchaseFail'));
      setPending(false);
    }
  }, [amount, orderId, paymentKey, postOrderInfoSuccess, postPaymentConfirm, t]);
  useEffect(() => {
    if (postPaymentConfirmSuccess) {
      postSubscription({ sensorId, plan: orderName });
    } else if (postPaymentConfirmSuccess === false) {
      window.alert(t('Devices.message.purchaseFail'));
      setPending(false);
    }
  }, [orderName, postPaymentConfirmSuccess, postSubscription, sensorId, t]);
  useEffect(() => {
    if (postSubscriptionSuccess) {
      onClose('updated');
    } else if (postSubscriptionSuccess === false) {
      window.alert(t('Devices.message.purchaseFail'));
      setPending(false);
    }
  }, [onClose, postSubscriptionSuccess, t]);

  async function onCheckoutClick() {
    const orderId = faker.string.nanoid();
    setOrderId(orderId);

    try {
      setPending(true);

      const res = await widgets.requestPayment({
        orderId,
        orderName,
        customerEmail: userInfo.email,
        customerName: userInfo.name,
      });
      setPaymentKey(res.paymentKey);
      postOrderInfo({ orderId, amount });
    } catch (error) {
      const { message } = error as TossError;
      window.alert(message);
      setPending(false);
    }
  }

  return (
    <>
      <Stack gap={2} sx={{ width: 480, p: 4 }}>
        <Stack>
          <Typography variant="h5" fontWeight="bold">
            {orderName}
          </Typography>
          <Typography>{amount.toLocaleString()}원</Typography>
        </Stack>
        <Box id="payment-method" />
        <Box id="agreement" />
        <Button variant="contained" disabled={!ready} onClick={onCheckoutClick}>
          {t('Devices.label.checkout')}
        </Button>
      </Stack>

      <Backdrop open={pending} sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}>
        <CircularProgress color="primary" />
      </Backdrop>
    </>
  );
}
